import React, { forwardRef, useState } from "react";
import {
  StyleSheet,
  View,
  TextInput as Input,
  TextStyle,
  Platform,
  TouchableOpacity,
} from "react-native";
import { Text } from "../../text/text";
import { Icon, Icons, fontFamily, typography, theme } from "ui-components";
import { fontWeights } from "ui-components/dist/theme/typography";

interface IEditTextInput {
  onChangeText: any;
  onFocus?: any;
  value?: any;
  style?: any;
  error?: any;
  disabled?: boolean;
  multiline?: any;
  numberOfLines?: number;
  iconName?: any;
  keyboardType?: any;
  maxLength?: any;
  mode?: any;
  render?: any;
  testID?: any;
  accessibilityId?: any;
  returnKeyType?:
    | "default"
    | "none"
    | "done"
    | "go"
    | "next"
    | "search"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  inputAccessoryViewID?: string;
  onSubmitEditing?: any;
  accessibilityLabel?: string | undefined;
  labelStyle?: TextStyle;
  label?: string;
  supportingText?: string;
  placeholder?: string;
  onBlur?: any;
  rightIcon?: Icons;
  rightIconColor?: string;
  rightIconSize?: number;
  renderLeftIcon?: any;
  renderRightIcon?: any;
  focused?: boolean;
  rightIconVisible?: boolean;
  autoFocus?: boolean;
  maxTextLength?: number;
}

export const EditTextInput = forwardRef(
  (
    {
      style,
      error,
      render,
      value,
      disabled,
      iconName,
      testID,
      accessibilityId,
      multiline = false,
      numberOfLines = 1,
      keyboardType = "default",
      maxLength,
      onChangeText,
      onFocus,
      placeholder,
      returnKeyType,
      inputAccessoryViewID,
      onSubmitEditing,
      accessibilityLabel = "",
      labelStyle = {},
      label,
      onBlur,
      supportingText,
      rightIcon,
      rightIconColor,
      rightIconSize,
      renderLeftIcon,
      renderRightIcon,
      focused = false,
      rightIconVisible = true,
      autoFocus = true,
      maxTextLength = 15,
      ...otherProps
    }: IEditTextInput,
    ref: any
  ) => {
    const { iconSize, fontSize, spacing, colors } = theme;
    let [changeOutlineColor, setChangeOutlineColor] = useState(false);
    let [editable, setEditable] = useState(false);
    const onFocusHandler = () => {
      setChangeOutlineColor(true);
      if (onFocus) {
        onFocus();
      }
    };
    const onBlurHandler = () => {
      setChangeOutlineColor(false);
      setEditable(false);
      if (onBlur) {
        onBlur();
      }
    };

    rightIcon = error ? Icons["warning"] : rightIcon;
    rightIconColor = disabled ? colors.grey : rightIconColor ? rightIconColor : colors.primary;

    const styles = StyleSheet.create({
      labelStyle: {
        color: colors.darkerGrey,
        fontWeight: "700",
      },
      sectionStyle: {
        // flex: 1,
        flexDirection: "row",
        alignItems: "center",
      },
      textInputContainerStyle: {
        // flex: 1,
      },
      placeholderStyle: {
        // flex: 1,
        fontFamily: fontFamily.Lato,
        fontSize: fontSize.subHeading,
        color: colors.darkerGrey,
        outlineColor: Platform.OS == "web" && colors.transparent,
        borderColor: disabled
          ? colors.grey
          : error
          ? colors.alert
          : changeOutlineColor
          ? colors.darkerGrey
          : value?.length > 0
          ? colors.grey
          : theme.colors.darkerGrey,
        borderBottomWidth: 1,
        padding: 0,
        outlineStyle: "none",
      },
      textContainer: {
        borderBottomWidth: 1,
        borderColor: disabled ? colors.grey : colors.darkerGrey,
      },
      text: {
        fontWeight: fontWeights.Regular,
        color: disabled ? colors.grey : colors.darkerGrey,
      },
    });

    style = {
      ...style,
      backgroundColor: disabled ? colors.lightGrey : colors.white,
    };

    return (
      <>
        <View style={styles.sectionStyle}>
          {editable ? (
            <View style={styles.textInputContainerStyle}>
              <Input
                autoFocus={autoFocus}
                editable={!disabled}
                testID={testID}
                accessibilityLabel={accessibilityLabel}
                style={styles.placeholderStyle}
                placeholder={value || placeholder}
                placeholderTextColor={disabled ? colors.grey : colors.darkerGrey}
                multiline={multiline}
                numberOfLines={numberOfLines}
                keyboardType={keyboardType}
                maxLength={maxLength}
                onChangeText={onChangeText}
                onFocus={onFocusHandler}
                returnKeyType={returnKeyType}
                inputAccessoryViewID={inputAccessoryViewID}
                onSubmitEditing={onSubmitEditing}
                value={value}
                onBlur={onBlurHandler}
                ref={ref}
                {...otherProps}
              />
            </View>
          ) : (
            <TouchableOpacity
              onPress={() => setEditable(true)}
              disabled={disabled}
              activeOpacity={0.6}
              style={styles.textContainer}
              testID={"text-button-" + testID}
              accessibilityLabel={"text-button-" + accessibilityLabel}>
              <Text numberOfLines={1} style={[typography.subHeading, styles.text]}>
                {
                  // value.substring(0,length)
                  value
                    ? value.length > maxTextLength
                      ? `${value.substring(0, maxTextLength)}...`
                      : value
                    : placeholder
                }
              </Text>
            </TouchableOpacity>
          )}
          {rightIconVisible && (
            <TouchableOpacity
              onPress={() => {
                setEditable(true);
              }}
              disabled={disabled}
              style={{
                marginLeft: 10,
                padding: 3,
                paddingBottom: 0,
              }}
              activeOpacity={0.6}
              testID={"edit-icon-button-" + testID}
              accessibilityLabel={"edit-icon-button-" + accessibilityLabel}>
              <Icon
                name={rightIcon || Icons.edit}
                size={rightIconSize || iconSize.small}
                color={rightIconColor || colors.darkerGrey}
              />
            </TouchableOpacity>
          )}
        </View>
      </>
    );
  }
);
